import ManagedErrorToasts from '@/components/error/ManagedErrorToasts';
import AddNewScriptWrapper from '@/components/escript/token/AddNewScriptWrapper';
import { ReviewNewScript } from '@/components/escript/token/ReviewNewScript';
import { PurchaseStepper } from '@/components/products/purchaseStepper/PurchaseStepper';
import { useEScriptOrderMedicationController } from '@/hooks/escript/useEscriptOrderMedicationController';
import { useScriptTokenContext } from '@/hooks/escript/useScriptTokenContext';
import getCheckoutData from '@/hooks/patient/refill/getCheckoutData';
import PageLoadError from '@/hooks/patient/refill/page-errors';
import useCustomer from '@/hooks/user/useCustomer';
import PatientRefillLoadingSkeleton from '@/pages/umeds/order-medication/OrderMedicationLoadingSkeleton';
import { Logger } from '@/utils/logger';
import { Alert, AlertTitle, Box, Button, Stack, toast, Typography } from '@montugroup/design-system';
import AddIcon from '@mui/icons-material/Add';
import { useTheme } from '@mui/material';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { BrazeImageCard } from '@/components/braze/BrazeImageCard';
import {
  AvailableProductsList,
  CantOrderDevices,
  CantOrderProducts,
  MobileOrderActions,
  ProductMatchIssues,
  UnavailableProductsSection,
} from '@/components/escript/order-medication';
import { BackButton } from '@/components/order/BackButton';
import RefillOrderSummary from '@/components/order/order-summary/RefillOrderSummary';
import { ProductList, ProductSection } from '@/components/products/refill/ProductSection';
import { FF_INLINE_UNAVAILABLE_PRODUCTS } from '@/constants/featureFlags';
import { RECOMMENDED_DEVICE_MAX_QUANTITY, RRP_DISCLAIMER } from '@/constants/order';
import { useReconcilePrescriptionsFFWrapper } from '@/hooks/escript/useReconcilePrescriptions';
import { useCrossSell } from '@/hooks/patient/refill/useCrossSell';
import { useGetCartEventInfo } from '@/hooks/products/useCartEventInfo';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { getImageUrlWithFallback } from '@montugroup/circuit-shared/components/product-detail/utils';
import useUser from '@montugroup/circuit-shared/hooks/user/useUser';
import { DesktopOnly, DisclaimerRRP, PageContent, PageSection } from './OrderMedication.style';
import { getProductCategoryValues } from './util';

export function OrderMedication() {
  const { data: user } = useUser();
  const { isLoading: customerIsLoading, data: customer } = useCustomer();
  const location = useLocation();
  const theme = useTheme();
  const enableBrazeContentCards = useFeatureFlag('braze-content-cards');
  const enableEscriptReconcilePrescriptions = useFeatureFlag('escript_reconcile_prescriptions');
  // Flag ON: unavailable items render inline at the bottom of the section that
  // would have held them, replacing the "Can't order now" accordion.
  const inlineUnavailableProducts = Boolean(useFeatureFlag(FF_INLINE_UNAVAILABLE_PRODUCTS));

  const orderMedicationController = useEScriptOrderMedicationController();
  const { refillResponse, selectedProducts, isSubmitting, hasSelectedAnyProducts } = orderMedicationController.state;
  const { submitOrder, validateOrder, enqueueError, refetchRefillResponse } = orderMedicationController.actions;

  const refillProducts = useMemo(
    () => [...(refillResponse.data?.medications ?? []), ...(refillResponse.data?.devices ?? [])],
    [refillResponse.data],
  );
  const getCartEventInfo = useGetCartEventInfo(refillProducts);

  const { attemptCrossSell, showCrossSellPage, crossSellableProducts, resetCrossSell, getRecommendation } =
    useCrossSell(selectedProducts.map((product) => ({ ...product, quantity: product.data.quantity })));

  // Get prescription IDs from selected products for reconciliation
  const prescriptionIds = selectedProducts
    .map((product) => product.data.prescriptionId)
    .filter((id): id is number => id !== undefined);

  const { mutateAsync: mutateAsyncReconcilePrescriptions } = useReconcilePrescriptionsFFWrapper(prescriptionIds);

  const {
    addNewScriptsState,
    setAddNewScriptsState,
    reviewNewScriptsState,
    setReviewNewScriptsState,
    newSubmittedScripts,
  } = useScriptTokenContext();

  // Get product categories
  const productCategories = getProductCategoryValues(refillResponse.data);
  const { availableProducts, productMatchIssues, cantOrderProducts, cantOrderDevices, otherProducts } =
    productCategories;

  const checkoutData = getCheckoutData(selectedProducts, []);
  const isCheckoutDisabled = customerIsLoading || isSubmitting || !hasSelectedAnyProducts;
  const shouldExpandUnavailableProducts =
    availableProducts.length === 0 && (productMatchIssues.length > 0 || cantOrderProducts.length > 0);
  const otherProductsCount = otherProducts.length + (inlineUnavailableProducts ? cantOrderDevices.length : 0);

  // Event handlers
  const handleNavClick = useCallback(() => {
    setAddNewScriptsState(false);
    setReviewNewScriptsState(false);
    refetchRefillResponse();
  }, [setAddNewScriptsState, setReviewNewScriptsState, refetchRefillResponse]);

  const handleAddNewScript = useCallback(() => {
    setAddNewScriptsState(true);
    setReviewNewScriptsState(false);
  }, [setAddNewScriptsState, setReviewNewScriptsState]);

  const handleSubmit = async () => {
    const isCrossSelling = !showCrossSellPage && (await attemptCrossSell());

    if (showCrossSellPage || !isCrossSelling) {
      try {
        // Reconcile prescriptions before proceeding
        if (enableEscriptReconcilePrescriptions && prescriptionIds.length > 0) {
          const reconciliationResult = await mutateAsyncReconcilePrescriptions();

          const reconciliationData = reconciliationResult?.body?.data;

          // If valid = false, refetch the refill endpoint to update the cart
          if (
            reconciliationData &&
            !reconciliationData.valid &&
            reconciliationData.errors &&
            reconciliationData.errors.length > 0
          ) {
            await refetchRefillResponse();
            toast.warning('Item(s) removed from cart due to invalid script token.');
            return;
          }
        }

        await submitOrder({ validateOrderCallback: validateOrder, customer: customer ?? undefined });
        showCrossSellPage && resetCrossSell();
      } catch (error: unknown) {
        enqueueError({
          title: 'Error Submitting Order',
          body: (error as Error).message,
        });
      }
    }
  };

  // Effects
  useEffect(() => {
    if (location.state?.addNewScript) {
      setAddNewScriptsState(true);
      setReviewNewScriptsState(false);
    }
  }, [location.state?.addNewScript, setAddNewScriptsState, setReviewNewScriptsState]);

  useEffect(() => {
    if (typeof refillResponse.data !== 'undefined') {
      new Logger('eScriptOrderMedication').info(`userId=${user!.id} loaded refill page`);
    }
  }, [refillResponse.data, user]);

  const title = showCrossSellPage ? 'Consider with your treatment' : 'Order medication';

  return (
    <>
      <ManagedErrorToasts />
      {showCrossSellPage && <BackButton action={resetCrossSell} />}
      {/* Loading State */}
      {refillResponse.isLoading && <PatientRefillLoadingSkeleton />}
      <PageContent>
        {/* Error State */}
        {refillResponse.isError && (
          <Alert severity="error">
            <AlertTitle>{PageLoadError.title}</AlertTitle>
            <div>{PageLoadError.body}</div>
          </Alert>
        )}

        {/* Add New Script */}
        {addNewScriptsState && (
          <>
            <BackButton action={handleNavClick} />
            <PageSection sx={{ gridArea: 'products' }}>
              <AddNewScriptWrapper />
            </PageSection>
          </>
        )}

        {/* Review New Script */}
        {reviewNewScriptsState && (
          <>
            <BackButton action={handleNavClick} />
            <PageSection sx={{ gridArea: 'products' }}>
              <ReviewNewScript script={newSubmittedScripts} setAddNewScriptState={handleAddNewScript} />
            </PageSection>
            <Box sx={{ gridArea: 'purchase' }}>
              <DesktopOnly>
                <Box sx={{ position: 'relative', width: '100%' }}>
                  <Box
                    sx={(theme) => ({
                      position: 'sticky',
                      top: '8rem',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: theme.spacing(5),
                    })}
                  >
                    <Button
                      component="button"
                      variant="outlined"
                      size="large"
                      color="primary"
                      startIcon={<AddIcon />}
                      data-testid="add-new-script-button"
                      onClick={(event) => {
                        event.preventDefault();
                        handleAddNewScript();
                      }}
                    >
                      Add new script
                    </Button>
                  </Box>
                </Box>
              </DesktopOnly>
            </Box>
          </>
        )}

        {/* Main Order Medication */}
        {refillResponse.isSuccess && !addNewScriptsState && !reviewNewScriptsState && (
          <>
            <PageSection sx={{ gridArea: 'title' }}>
              <Typography variant="h5" component="h1" fontWeight={(theme) => theme.typography.fontWeightBold}>
                {title}
              </Typography>
            </PageSection>

            <PageSection sx={{ gridArea: 'steps', mb: 3 }}>
              <Stack direction="column" gap={theme.spacing(6)}>
                {enableBrazeContentCards && <BrazeImageCard />}
                <PurchaseStepper stage="Order" />
              </Stack>
            </PageSection>

            {showCrossSellPage && crossSellableProducts && Boolean(crossSellableProducts?.length) ? (
              <PageSection sx={{ gridArea: 'products' }}>
                <ProductList
                  products={crossSellableProducts.map((crossSellableProduct, position) => ({
                    ...crossSellableProduct,
                    productName: crossSellableProduct.name,
                    supplier: crossSellableProduct.supplierName || crossSellableProduct.brand || undefined,
                    maxQuantity: RECOMMENDED_DEVICE_MAX_QUANTITY,
                    cartEventInfo: getCartEventInfo(crossSellableProduct.id, getRecommendation(position)),
                    ...(crossSellableProduct.imageUrl && {
                      image: {
                        src: getImageUrlWithFallback({
                          imageUrl: crossSellableProduct.imageUrl,
                          width: 60,
                          height: 60,
                        }),
                      },
                    }),
                  }))}
                />
              </PageSection>
            ) : (
              <PageSection sx={{ gridArea: 'products' }}>
                <AvailableProductsList
                  products={availableProducts}
                  hideEmptyAlert={inlineUnavailableProducts && cantOrderProducts.length > 0}
                />

                {inlineUnavailableProducts ? (
                  <>
                    <CantOrderProducts products={cantOrderProducts} />
                    <ProductMatchIssues issues={productMatchIssues} />
                  </>
                ) : (
                  <UnavailableProductsSection
                    productMatchIssues={productMatchIssues}
                    cantOrderDevices={cantOrderDevices}
                    cantOrderProducts={cantOrderProducts}
                    showExpanded={shouldExpandUnavailableProducts}
                  />
                )}

                {otherProductsCount > 0 && (
                  <ProductSection
                    label="Other Products"
                    count={otherProductsCount}
                    products={otherProducts.map((product) => ({
                      ...product,
                      productName: product.name,
                      supplier: product.supplier || product.brand || undefined,
                      maxQuantity: RECOMMENDED_DEVICE_MAX_QUANTITY,
                      cartEventInfo: getCartEventInfo(product.id),
                    }))}
                  >
                    {inlineUnavailableProducts && <CantOrderDevices devices={cantOrderDevices} />}
                  </ProductSection>
                )}
              </PageSection>
            )}

            <Box sx={{ gridArea: 'purchase' }}>
              <DesktopOnly>
                <Box sx={{ position: 'relative', width: '100%' }}>
                  <Box
                    sx={(theme) => ({
                      position: 'sticky',
                      top: '8rem',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: theme.spacing(5),
                    })}
                  >
                    <Button
                      component="button"
                      variant="outlined"
                      size="large"
                      color="primary"
                      startIcon={<AddIcon />}
                      data-testid="add-new-script-button"
                      onClick={(event) => {
                        event.preventDefault();
                        handleAddNewScript();
                      }}
                    >
                      Add new script
                    </Button>
                    <RefillOrderSummary
                      checkoutData={checkoutData}
                      onCheckout={handleSubmit}
                      disabled={isCheckoutDisabled}
                    />
                  </Box>
                </Box>
              </DesktopOnly>
              <DisclaimerRRP>
                <Typography variant="caption">{RRP_DISCLAIMER}</Typography>
              </DisclaimerRRP>
            </Box>
          </>
        )}
      </PageContent>

      {/* Mobile Actions */}
      <MobileOrderActions
        viewState={
          reviewNewScriptsState
            ? 'reviewNewScript'
            : refillResponse.isSuccess && !addNewScriptsState && !reviewNewScriptsState
              ? 'orderMedication'
              : 'loading'
        }
        checkoutData={checkoutData}
        isCheckoutDisabled={isCheckoutDisabled}
        onAddNewScript={handleAddNewScript}
        onCheckout={handleSubmit}
      />
    </>
  );
}
